import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { 
  Briefcase, 
  Users, 
  TrendingUp, 
  ArrowRight, 
  FileText, 
  Sparkles,
  Target,
  Zap,
  Plus,
  BookOpen,
  Download,
  HelpCircle
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { useUser } from "../context/UserContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Demo stats for guest users
const DEMO_STATS = {
  total_jobs: 3,
  total_candidates: 35,
  strong_matches: 12,
  medium_matches: 15,
  low_matches: 8,
  recent_candidates: [],
};

// Demo jobs for guest users
const DEMO_JOBS = [
  {
    id: "demo-1",
    title: "Desarrollador Full Stack",
    description: "Buscamos desarrollador con experiencia en React y Node.js",
    required_skills: ["React", "Node.js", "MongoDB"],
    candidate_count: 12,
    isDemo: true,
  },
  {
    id: "demo-2",
    title: "Diseñador UX/UI",
    description: "Diseñador creativo para aplicación móvil",
    required_skills: ["Figma", "User Research"],
    candidate_count: 8,
    isDemo: true,
  },
  {
    id: "demo-3",
    title: "Data Analyst",
    description: "Analista de datos para toma de decisiones",
    required_skills: ["SQL", "Python", "Excel"],
    candidate_count: 15,
    isDemo: true,
  },
];

export default function Dashboard() {
  const { user, requireEmail } = useUser();
  const [stats, setStats] = useState({
    total_jobs: 0,
    total_candidates: 0,
    strong_matches: 0,
    medium_matches: 0,
    low_matches: 0,
    recent_candidates: [],
  });
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [user?.email]);

  const fetchData = async () => {
    // If no user, show demo data
    if (!user?.email) {
      setStats(DEMO_STATS);
      setJobs(DEMO_JOBS);
      setLoading(false);
      return;
    }

    try {
      const params = `?user_email=${encodeURIComponent(user.email)}`;
      const [statsRes, jobsRes] = await Promise.all([
        axios.get(`${API}/stats${params}`),
        axios.get(`${API}/jobs${params}`),
      ]);
      setStats(statsRes.data);
      setJobs(jobsRes.data.slice(0, 3));
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const getMatchBadgeClass = (level) => {
    if (level === "Strong match") return "badge-strong";
    if (level === "Medium match") return "badge-medium";
    return "badge-low";
  };

  const handleJobClick = (job, e) => {
    if (job.isDemo) {
      e.preventDefault();
      requireEmail(() => {
        // After login, they'll see their own data
      });
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 w-48 bg-muted rounded" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-32 bg-muted rounded-2xl" />
          ))}
        </div>
      </div>
    );
  }

  const isGuestView = !user;
  const isEmpty = user && stats.total_jobs === 0 && stats.total_candidates === 0;

  return (
    <div className="space-y-8" data-testid="dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground">
            Dashboard
          </h1>
          <p className="text-muted-foreground mt-1">
            {user ? 'Bienvenido de vuelta' : 'Descubre cómo funciona TalentMatcher'}
          </p>
        </div>
        <Link to="/jobs/new">
          <Button className="rounded-full gap-2">
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Nuevo Puesto</span>
          </Button>
        </Link>
      </div>

      {/* Guest Banner */}
      {isGuestView && (
        <Card className="bg-primary/5 border-primary/20">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <Sparkles className="w-5 h-5 text-primary" />
              <div className="flex-1">
                <p className="text-sm font-medium">
                  Datos de demostración
                </p>
                <p className="text-xs text-muted-foreground">
                  Ingresa tu correo para crear tus propios puestos y gestionar candidatos reales
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Cards - Always visible */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="card-hover" data-testid="stat-jobs">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Briefcase className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-3xl font-heading font-bold text-foreground">
                  {stats.total_jobs}
                </p>
                <p className="text-sm text-muted-foreground">Puestos de Trabajo</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover" data-testid="stat-candidates">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-accent/30 flex items-center justify-center">
                <Users className="w-6 h-6 text-accent-foreground" />
              </div>
              <div>
                <p className="text-3xl font-heading font-bold text-foreground">
                  {stats.total_candidates}
                </p>
                <p className="text-sm text-muted-foreground">Candidatos</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover" data-testid="stat-matches">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-success/30 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-accent-foreground" />
              </div>
              <div>
                <p className="text-3xl font-heading font-bold text-foreground">
                  {stats.strong_matches}
                </p>
                <p className="text-sm text-muted-foreground">Matches Fuertes</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Empty State - Welcome Section */}
      {isEmpty && (
        <Card className="border-2 border-dashed border-primary/30 bg-gradient-to-br from-primary/5 to-transparent" data-testid="welcome-section">
          <CardContent className="py-12">
            <div className="text-center max-w-lg mx-auto">
              <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-6">
                <Sparkles className="w-10 h-10 text-primary" />
              </div>
              <h2 className="font-heading text-2xl font-bold mb-3">
                ¡Bienvenido a TalentMatcher!
              </h2>
              <p className="text-muted-foreground mb-6">
                Empieza a encontrar al candidato ideal para tu equipo. Crea tu primer puesto de trabajo y sube CVs para analizarlos con IA.
              </p>
              <Link to="/jobs/new">
                <Button size="lg" className="rounded-full gap-2">
                  <Plus className="w-5 h-5" />
                  Crear mi primer puesto
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      )}

      {/* How it works - Only show when empty */}
      {isEmpty && (
        <div>
          <h2 className="font-heading text-xl font-semibold mb-4">¿Cómo funciona?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="card-hover">
              <CardContent className="pt-6">
                <div className="flex flex-col items-center text-center">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                    <span className="font-heading font-bold text-primary">1</span>
                  </div>
                  <Target className="w-8 h-8 text-primary mb-3" />
                  <h3 className="font-heading font-semibold mb-2">Crea un puesto</h3>
                  <p className="text-sm text-muted-foreground">
                    Define el título, habilidades requeridas y nivel de experiencia
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="card-hover">
              <CardContent className="pt-6">
                <div className="flex flex-col items-center text-center">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                    <span className="font-heading font-bold text-primary">2</span>
                  </div>
                  <FileText className="w-8 h-8 text-primary mb-3" />
                  <h3 className="font-heading font-semibold mb-2">Sube CVs</h3>
                  <p className="text-sm text-muted-foreground">
                    Arrastra archivos PDF o DOCX para analizar candidatos
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="card-hover">
              <CardContent className="pt-6">
                <div className="flex flex-col items-center text-center">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                    <span className="font-heading font-bold text-primary">3</span>
                  </div>
                  <Zap className="w-8 h-8 text-primary mb-3" />
                  <h3 className="font-heading font-semibold mb-2">Obtén resultados</h3>
                  <p className="text-sm text-muted-foreground">
                    La IA analiza y puntúa cada candidato automáticamente
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Match Distribution - Only show when there are candidates */}
      {stats.total_candidates > 0 && (
        <Card data-testid="match-distribution">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Distribución de Matches</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-[#D4F571]" />
                <span className="text-sm">Fuertes: {stats.strong_matches}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-[#FCD34D]" />
                <span className="text-sm">Medios: {stats.medium_matches}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-muted" />
                <span className="text-sm">Bajos: {stats.low_matches}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Jobs - Only show when there are jobs */}
      {jobs.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-xl font-semibold">
              {isGuestView ? "Ejemplos de Puestos" : "Puestos Recientes"}
            </h2>
            <Link to="/jobs" data-testid="view-all-jobs">
              <Button variant="ghost" className="gap-2">
                Ver Todos <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>

          <div className="grid gap-4">
            {jobs.map((job) => (
              <Link
                key={job.id}
                to={job.isDemo ? "#" : `/jobs/${job.id}`}
                onClick={(e) => handleJobClick(job, e)}
                data-testid={`job-card-${job.id}`}
              >
                <Card className={`card-hover ${job.isDemo ? "opacity-90" : ""}`}>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-heading font-semibold text-lg">
                            {job.title}
                          </h3>
                          {job.isDemo && (
                            <span className="text-xs bg-secondary text-muted-foreground px-2 py-0.5 rounded-full">
                              Ejemplo
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground line-clamp-1 mt-1">
                          {job.description || "Sin descripción"}
                        </p>
                        <div className="flex items-center gap-4 mt-3">
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <FileText className="w-3 h-3" />
                            {job.required_skills.length} habilidades
                          </span>
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            {job.candidate_count} candidatos
                          </span>
                        </div>
                      </div>
                      <ArrowRight className="w-5 h-5 text-muted-foreground" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Recent Candidates */}
      {stats.recent_candidates.length > 0 && (
        <div>
          <h2 className="font-heading text-xl font-semibold mb-4">
            Candidatos Recientes
          </h2>
          <div className="grid gap-3">
            {stats.recent_candidates.map((candidate) => (
              <Link
                key={candidate.id}
                to={`/candidates/${candidate.id}`}
                data-testid={`candidate-card-${candidate.id}`}
              >
                <Card className="card-hover">
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
                          <span className="font-heading font-semibold text-sm">
                            {candidate.data?.name?.charAt(0) || "?"}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium">
                            {candidate.data?.name || "Desconocido"}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {candidate.file_name}
                          </p>
                        </div>
                      </div>
                      <Badge
                        className={getMatchBadgeClass(
                          candidate.match_score?.match_level
                        )}
                      >
                        {candidate.match_score?.overall_score || 0}%
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Help Section - Quick access to user guide */}
      <Card className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
        <CardContent className="py-6">
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-primary/20 flex items-center justify-center shrink-0">
              <BookOpen className="w-7 h-7 text-primary" />
            </div>
            <div className="flex-1 text-center sm:text-left">
              <h3 className="font-heading font-semibold text-lg mb-1">
                ¿Necesitás ayuda?
              </h3>
              <p className="text-sm text-muted-foreground">
                Descubrí todas las funcionalidades de TalentMatcher: comparador de candidatos, Smart Shortlist, evaluaciones sugeridas y más.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <a 
                href="/GUIA_USUARIO_TalentMatcher.pdf" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex"
              >
                <Button variant="outline" className="gap-2">
                  <Download className="w-4 h-4" />
                  Descargar Guía PDF
                </Button>
              </a>
              <Link to="/help">
                <Button variant="ghost" className="gap-2 text-primary">
                  <HelpCircle className="w-4 h-4" />
                  Centro de Ayuda
                </Button>
              </Link>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
